import { AfterRenderRef, Component, DestroyRef, Injector, OnDestroy, OnInit, ViewChild, afterNextRender, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { of } from 'rxjs';
import { finalize, startWith, switchMap, take } from 'rxjs/operators';
import { MemberSearch, MemberSearchResults } from '@roosevelt/elig-ui-lib';
import { BmtSearchComponent } from '@roosevelt/client-ui-lib';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { AuthorizationsAsync } from '../authorizations/authorizations-async';
import { IUiAuthorizations } from '../authorizations/state';
import { ILoginState } from '../login/store/state';
import {
  buildSelectedSubscriber,
  isAddFlowHandoffEvent,
  normalizeFoundMemberForPrefill,
  resolveAddFlowHandoffFoundMember,
  resolveMemberId,
  resolveSubscriberEnrollmentId,
  resolveSubscriberName
} from '../shared/member-feed.util';
import { createFeatureManagedClientSearchController, provideClientSearchStatePrefix } from '../shared/client-search-render.controller';
import { clearVisibilityTimer, createConfirmSsnForm, isCopyShortcut, restartVisibilityTimer } from '../shared/utils/ssn-form.util';
import { IClientPlanDetails } from '../store/massActions/state';
import { futureDate } from '../utils/validations/field-rules';
import { PseudoSsnApiService } from '../store/pseudoSsn/api.service';

const MANAGE_ELIGIBILITY_CLIENT_SEARCH_PREFIX = 'manage-eligibility-';
const MEMBER_DETAILS_STORAGE_KEY = 'member-details-state';
const MEMBER_DETAILS_BMT_KEY = 'BMT';

type DropDownOption = { label: string; value: string };
type MemberIdEntryMode = 'manual' | 'system' | 'both';

type SubClientSelection = {
  planAcronym: string;
  specifiedId: string;
  asOfDate: string;
  name?: string;
  client: {
    name?: string;
    planAcronym: string;
    specifiedId: string;
  };
};

export type ManageEligibilityFlow = 'Member' | 'Add Member' | 'Family Enrollment';

export type ManageEligibilityMassActionStorePayload = {
  flow: ManageEligibilityFlow;
  massActionState: Record<string, unknown>;
  selectedSubClient?: SubClientSelection;
};

export type ManageEligibilityMemberStorePayload = Record<string, unknown> & {
  event?: unknown;
  nextRoute?: '/family-enrollment' | '/add-member';
};

type FindMemberFeed = {
  planAcronym: string;
  clientId: string;
  subClientId: string;
  memberId: string | number;
  adminPlan: string;
  pseudoSsnInd?: boolean;
};

type MemberDetailsFeed = {
  subscriberName: string;
  planAcronym: string;
  clientId: string;
  subClientId: string;
  memberId: string | number;
  subscriberEnrollmentID: string | number;
  foundMember?: unknown;
  asOf: string;
};

type ManageEligibilityLuxState = {
  login: ILoginState;
  clientSearch: unknown;
  memberDetails?: Record<string, {
    findMemberState?: {
      foundMember?: unknown;
    };
  } | undefined>;
};

@Component({
  selector: 'app-manage-eligibility-page',
  templateUrl: './manage-eligibility.component.html',
  styleUrls: ['./manage-eligibility.component.scss'],
  providers: [provideClientSearchStatePrefix(MANAGE_ELIGIBILITY_CLIENT_SEARCH_PREFIX)],
  standalone: false
})
export class ManageEligibilityComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly lux = inject<Lux<ManageEligibilityLuxState>>(Lux);
  private clientSearchComponent?: BmtSearchComponent;
  private readonly routeId = toSignal(this.activeRoute.params, {
    initialValue: this.activeRoute.snapshot.params,
    injector: this.injector
  });
  private readonly pendingFoundMemberEvent = signal<string | null>(null);
  private readonly pendingFoundMemberResolutionTick = signal(0);
  private readonly clientSearchResetVersion = signal(0);
  private lastRouteId?: string;
  private initialized = false;
  private pendingFoundMemberResolutionRef: AfterRenderRef | null = null;
  private pendingFoundMemberResolutionAttempts = 0;
  private pendingSubClientSelectionRef: AfterRenderRef | null = null;
  private pendingSubClientSelection: SubClientSelection[] | null = null;
  private cachedFoundMemberAtSearchStart: unknown = null;
  private strictAuthorizationRequestVersion = 0;
  private copyErrorTimer: number | null = null;

  readonly setMassActionStore = output<ManageEligibilityMassActionStorePayload>();
  readonly setMemberStore = output<ManageEligibilityMemberStorePayload>();
  readonly cachedFoundMember = toSignal(
    this.lux.get(
      (state: ManageEligibilityLuxState) => state.memberDetails?.['BMT']?.findMemberState?.foundMember,
      `${MANAGE_ELIGIBILITY_CLIENT_SEARCH_PREFIX}found-member`
    ),
    { initialValue: null as unknown }
  );
  readonly availableSubClients = toSignal(
    this.lux.get(
      state => ((state as ManageEligibilityLuxState & {
        clientSearch?: {
          subClientInfo?: {
            subClients?: Array<{ specifiedId?: string }>;
          };
        };
      }).clientSearch?.subClientInfo?.subClients ?? []),
      `${MANAGE_ELIGIBILITY_CLIENT_SEARCH_PREFIX}subclients`
    ),
    { initialValue: [] as Array<{ specifiedId?: string }> }
  );
  readonly showCopyError = signal(false);
  readonly appUiAuthLoading = input(false);
  readonly strictUiAuthLoading = signal(false);
  readonly manageEligFeatures = input<string[]>([]);
  readonly isFetchingUiAuth = computed(() => this.strictUiAuthLoading()
    || (!this.manageEligFeatures().length && this.appUiAuthLoading()));
  readonly showContractLoader = computed(() => this.subHeader() === 'Family Enrollment'
    && this.selectedSubClients().length > 0
    && this.isFecthingContractInfo());
  readonly showBlockingLoader = computed(() => this.showContractLoader() || this.isFetchingUiAuth());
  readonly changeType = computed<'text' | 'password'>(() => {
    const ssnValue = this.ssnValue() || '';
    return ssnValue.length >= 9 ? 'password' : 'text';
  });
  readonly displayAddMemberForm = signal(false);
  readonly contractResponseInput = input<IClientPlanDetails | null>(null);
  readonly contractResponse = computed(() => this.contractResponseInput());
  readonly showSsnFormForFamilyEnroll = signal(false);
  readonly disableSetDefaults = signal(false);
  readonly isFecthingContractInfo = input(false);
  readonly familyEnrollmentAuthorized = signal(true);
  readonly displayFamilyEnrollDefaultsForm = computed(() => this.subHeader() === 'Family Enrollment'
    && this.familyEnrollmentAuthorized()
    && this.selectedSubClients().length > 0);
  readonly selectedSubClients = signal<SubClientSelection[]>([]);
  readonly subHeader = computed(() => {
    const currentRouteId = this.routeId()?.['id'];
    if (currentRouteId === 'add-member') {
      return 'Add Member';
    }
    if (currentRouteId === 'family-enrollment') {
      return 'Family Enrollment';
    }
    return 'Member';
  });
  readonly addMemberForm = signal<UntypedFormGroup | null>(null);
  readonly memberIdEntryMode = signal<MemberIdEntryMode>('manual');
  readonly isGeneratingPseudoSsn = signal(false);
  readonly pseudoSsnError = signal('');

  private readonly ssnValue = toSignal(
    toObservable(this.addMemberForm, { injector: this.injector }).pipe(
      switchMap(addMemberForm => {
        const control = addMemberForm?.get('ssn');
        return control
          ? control.valueChanges.pipe(startWith(control.value || ''))
          : of('');
      })
    ),
    { initialValue: '', injector: this.injector }
  );
  readonly setDefaultsForm = signal<UntypedFormGroup | null>(null);
  readonly selectedAdminPlan = input('');
  readonly clientSearchMountKey = computed(() => `${this.selectedAdminPlan()}|${this.subHeader()}|${this.clientSearchResetVersion()}`);
  readonly clientSearch = createFeatureManagedClientSearchController({
    lux: this.lux,
    storagePrefix: MANAGE_ELIGIBILITY_CLIENT_SEARCH_PREFIX,
    selectedAdminPlan: this.selectedAdminPlan,
    remountTrigger: this.clientSearchMountKey,
    isActive: () => this.initialized,
    canRenderWhenAuthorized: () => this.subHeader() !== 'Member'
  });
  readonly findMemberFeed = signal<FindMemberFeed | null>(null);
  readonly eligStatusReasons = input<DropDownOption[]>([]);
  readonly qualifierTypes = input<DropDownOption[]>([]);
  readonly memberBenefitTypes = input<DropDownOption[]>([]);
  readonly memberSearchFeatures: MemberSearch[] = [MemberSearch.basic, MemberSearch.asOfDate, MemberSearch.singleName, MemberSearch.bmtSearch];
  readonly memberSearchResultFeatures: MemberSearchResults[] = [MemberSearchResults.personal, MemberSearchResults.identifiers, MemberSearchResults.planIdentity, MemberSearchResults.status];

  @ViewChild(BmtSearchComponent)
  set clientSearchComponentRef(value: BmtSearchComponent | undefined) {
    this.clientSearchComponent = value;
    if (value) {
      this.syncSingleAvailableSubgroupSelection(this.availableSubClients());
    }
  }

  constructor(
    private readonly route: Router,
    private readonly fb: UntypedFormBuilder,
    private readonly activeRoute: ActivatedRoute,
    private readonly uiAuthAsync: AuthorizationsAsync,
    private readonly pseudoSsnApi: PseudoSsnApiService

  ) {
    effect(() => {
      const shouldShowFamilyDefaults = this.displayFamilyEnrollDefaultsForm();
      const contractResponse = this.contractResponse();
      this.eligStatusReasons();
      this.qualifierTypes();
      this.memberBenefitTypes();

      if (!shouldShowFamilyDefaults || !contractResponse) {
        this.setDefaultsForm.set(null);
        this.showSsnFormForFamilyEnroll.set(false);
        return;
      }

      this.setFamilyEnrollDefaultsForm();
    });

    effect(() => {
      const currentRouteId = this.routeId()?.['id'];
      if (currentRouteId === this.lastRouteId) {
        return;
      }

      this.lastRouteId = currentRouteId;
      this.clearPendingSubClientSelection();
      this.invalidateStrictAuthorizationRequest();
      this.pendingFoundMemberEvent.set(null);
      this.clearPendingFoundMemberResolution();
      this.cachedFoundMemberAtSearchStart = null;
      this.selectedSubClients.set([]);
      this.findMemberFeed.set(null);
      this.displayAddMemberForm.set(false);
      this.showSsnFormForFamilyEnroll.set(false);
      this.disableSetDefaults.set(false);
      this.familyEnrollmentAuthorized.set(true);
      this.resetForms();
    });

    effect(() => {
      const pendingFoundMemberEvent = this.pendingFoundMemberEvent();
      if (!pendingFoundMemberEvent) {
        this.pendingFoundMemberResolutionAttempts = 0;
        this.clearPendingFoundMemberResolution();
        return;
      }

      this.pendingFoundMemberResolutionTick();
      this.schedulePendingFoundMemberResolution();
    });

    effect(() => {
      this.syncSingleAvailableSubgroupSelection(this.availableSubClients());
    });
  }

  ngOnInit(): void {
    this.initialized = true;
    this.clientSearch.scheduleRender(true);
  }

  resetSearch(): void {
    this.clientSearchResetVersion.update(version => version + 1);
    this.cachedFoundMemberAtSearchStart = null;
    this.clientSearchReset('reset');
  }

  forFlowsOtherThanMassActions(_page: string): void {
    this.selectedSubClients.set([]);
    this.displayAddMemberForm.set(false);
    this.showSsnFormForFamilyEnroll.set(false);
    this.disableSetDefaults.set(false);
    this.familyEnrollmentAuthorized.set(true);
    this.resetForms();
    this.resetSearch();
  }

  clientSelected(event: unknown): void {
    if (!this.matchesPendingSubClientSelectionClient(event)) {
      this.clearPendingSubClientSelection();
    }

    this.invalidateStrictAuthorizationRequest();
    this.pendingFoundMemberEvent.set(null);
    this.clearPendingFoundMemberResolution();
    this.selectedSubClients.set([]);
    this.displayAddMemberForm.set(false);
    this.showSsnFormForFamilyEnroll.set(false);
    this.disableSetDefaults.set(false);
    this.familyEnrollmentAuthorized.set(true);
    this.findMemberFeed.set(null);
    this.resetForms();
  }

  subClientSelected(event: unknown): void {
    const selectedSubClients = Array.isArray(event) ? [...event as SubClientSelection[]] : [];

    this.clearPendingSubClientSelection();
    this.pendingSubClientSelection = selectedSubClients;
    this.pendingSubClientSelectionRef = afterNextRender(() => {
      const pendingSubClientSelection = this.pendingSubClientSelection;
      this.pendingSubClientSelection = null;
      this.pendingSubClientSelectionRef = null;

      if (!pendingSubClientSelection) {
        return;
      }

      this.applySubClientSelection(pendingSubClientSelection);
    }, { injector: this.injector });
  }

  goToMemberDetails(event: any): void {
    const normalizedSelectedSubscriber = buildSelectedSubscriber(
      event,
      resolveSubscriberEnrollmentId(event)
    );
    const { asOf: _ignoredAsOf, ...memberRouteSelectedSubscriber } = normalizedSelectedSubscriber;
    const selectedSubscriber = this.subHeader() === 'Member'
      ? memberRouteSelectedSubscriber
      : normalizedSelectedSubscriber;
    const addMemberFeed = this.buildMemberDetailsFeed(normalizedSelectedSubscriber);
    const planAcronym = selectedSubscriber?.planAcronym;
    const clientSpecifiedIdentifier = selectedSubscriber?.clientSpecifiedIdentifier;
    const subclientSpecifiedIdentifier = selectedSubscriber?.subclientSpecifiedIdentifier;

    if (!planAcronym || !clientSpecifiedIdentifier || !subclientSpecifiedIdentifier) {
      return;
    }

    const appContext = `${this.selectedAdminPlan()}/${planAcronym}/${clientSpecifiedIdentifier}/${subclientSpecifiedIdentifier}`;
    this.setMemberStore.emit({
      selectedSubscriber,
      ...(addMemberFeed && { addMemberFeed })
    });
    this.callStrictAuthoriztions(appContext, 'member-details');
  }

  callStrictAuthoriztions(appContext: string, flow?: string): void {
    const requestVersion = ++this.strictAuthorizationRequestVersion;
    this.strictUiAuthLoading.set(true);

    this.uiAuthAsync.getAuthorizations('strict', appContext)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (requestVersion === this.strictAuthorizationRequestVersion) {
            this.strictUiAuthLoading.set(false);
          }
        })
      )
      .subscribe(uiAuth => {
        if (requestVersion !== this.strictAuthorizationRequestVersion) {
          return;
        }

        if (flow === 'member-details' && Object.keys(uiAuth || {}).length) {
          void this.route.navigateByUrl('/details');
          return;
        }

        if (this.subHeader() === 'Add Member') {
          const allowed = this.hasManageEligibilityPermission(uiAuth, 'addMember');
          this.displayAddMemberForm.set(allowed);
          if (allowed) {
            this.setConfirmSsnForm();
            if (this.memberIdEntryMode() === 'system') {
              this.generatePseudoSsn();
            }
          }
        }

        if (this.subHeader() === 'Family Enrollment') {
          const allowed = this.hasManageEligibilityPermission(uiAuth, 'enrollFamily')
            || this.hasManageEligibilityPermission(uiAuth, 'addMember');
          this.familyEnrollmentAuthorized.set(allowed);
          if (!allowed) {
            this.showSsnFormForFamilyEnroll.set(false);
            this.disableSetDefaults.set(false);
            this.setDefaultsForm.set(null);
          }
        }
      });
  }

  clientSearchReset(_event: unknown): void {
    this.clearPendingSubClientSelection();
    this.invalidateStrictAuthorizationRequest();
    this.pendingFoundMemberEvent.set(null);
    this.clearPendingFoundMemberResolution();
    this.cachedFoundMemberAtSearchStart = null;
    this.selectedSubClients.set([]);
    this.displayAddMemberForm.set(false);
    this.showSsnFormForFamilyEnroll.set(false);
    this.disableSetDefaults.set(false);
    this.familyEnrollmentAuthorized.set(true);
    this.findMemberFeed.set(null);
    this.resetForms();
  }

  setConfirmSsnForm(): void {
    const addMemberForm = createConfirmSsnForm(this.fb);
    this.addMemberForm.set(addMemberForm);
  }

  onKeyPress($event: Event): void {
    const keyboardEvent = $event as KeyboardEvent;
    if (isCopyShortcut(keyboardEvent)) {
      keyboardEvent.preventDefault();
      this.copyErrorTimer = restartVisibilityTimer(this.showCopyError, this.copyErrorTimer, () => {
        this.copyErrorTimer = null;
      }, 5000);
    }
  }

  navigateToAddMember() {
    const addMemberForm = this.addMemberForm();
    if (!addMemberForm) {
      return;
    }

    this.navigateToAddMemberWithId(addMemberForm.value.confirmSsn, false);
  }

  navigateToAddMemberWithId(memberId: string, pseudoSsnInd: boolean): void {
    const selectedSubClient = this.selectedSubClients()[0];
    const addMemberForm = this.addMemberForm();
    if (!selectedSubClient || (!pseudoSsnInd && !addMemberForm?.valid)) {
      return;
    }

    this.cachedFoundMemberAtSearchStart = this.cachedFoundMember();
    this.clearPersistedFoundMember();
    this.findMemberFeed.set({
      planAcronym: selectedSubClient.planAcronym,
      clientId: selectedSubClient.client.specifiedId,
      subClientId: selectedSubClient.specifiedId,
      memberId,
      adminPlan: this.selectedAdminPlan(),
      pseudoSsnInd,
    });
  }

  generatePseudoSsn(): void {
    const selectedSubClient = this.selectedSubClients()[0];
    if (!selectedSubClient || this.isGeneratingPseudoSsn()) {
      return;
    }

    this.isGeneratingPseudoSsn.set(true);
    this.pseudoSsnError.set('');
    this.pseudoSsnApi.generate().pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.isGeneratingPseudoSsn.set(false);
      })
    ).subscribe({
      next: memberId => {
        if (selectedSubClient === this.selectedSubClients()[0] && memberId) {
          this.navigateToAddMemberWithId(memberId, true);
        } else if (!memberId) {
          this.pseudoSsnError.set('The pseudo SSN generation API did not return a member ID.');
        }
      },
      error: () => {
        this.pseudoSsnError.set('Unable to generate a pseudo SSN. Please try again.');
      }
    });
  }

  foundMember(event: unknown): void {
    if (event === 'cancel') {
      this.resetSearch();
      return;
    }

    if (isAddFlowHandoffEvent(event) && typeof event !== 'string') {
      this.setAddMemberFeed(event);
      return;
    }

    if (typeof event !== 'string') {
      this.goToMemberDetails(event);
      return;
    }

    this.pendingFoundMemberEvent.set(null);
    this.pendingFoundMemberResolutionAttempts = 0;
    this.pendingFoundMemberResolutionTick.set(0);
    this.pendingFoundMemberEvent.set(event);
  }

  setAddMemberFeed(event: unknown): void {
    const selectedSubClient = this.selectedSubClients()[0];
    const addMemberForm = this.addMemberForm();
    const defaultsForm = this.setDefaultsForm();
    if (!selectedSubClient) {
      return;
    }

    const isAddFlowEvent = isAddFlowHandoffEvent(event);
    const foundMember = isAddFlowEvent ? resolveAddFlowHandoffFoundMember(event) : event;
    const confirmedMemberId = addMemberForm?.value.confirmSsn;
    const enteredMemberId = isAddFlowEvent
      ? (this.findMemberFeed()?.memberId ?? confirmedMemberId ?? resolveMemberId(foundMember))
      : (resolveMemberId(foundMember) ?? confirmedMemberId);
    let rawFoundMember = event;
    if (isAddFlowEvent) {
      const addFlowFallbackFoundMember = typeof event === 'string'
        ? this.resolveCurrentFoundMember()
        : null;
      rawFoundMember = resolveAddFlowHandoffFoundMember(event) ?? addFlowFallbackFoundMember;
    }
    const normalizedFoundMember = normalizeFoundMemberForPrefill(rawFoundMember);
    const prefillFoundMember = isAddFlowEvent
      ? this.resolveAddFlowFoundMember(event, rawFoundMember, normalizedFoundMember)
      : normalizedFoundMember;
    const resolvedMemberId = isAddFlowEvent
      ? enteredMemberId
      : (resolveMemberId(prefillFoundMember) ?? enteredMemberId);
    const isFamilyEnrollmentFlow = this.subHeader() === 'Family Enrollment';
    const resolvedSubscriberEnrollmentId = isFamilyEnrollmentFlow
      ? (resolveSubscriberEnrollmentId(prefillFoundMember) ?? '')
      : '';
    const familyDefaults = defaultsForm?.getRawValue?.() ?? defaultsForm?.value ?? {};
    let nextRoute: '/family-enrollment' | '/add-member' | undefined;

    if (isAddFlowEvent) {
      nextRoute = isFamilyEnrollmentFlow ? '/family-enrollment' : '/add-member';
    }

    this.setMemberStore.emit({
      addMemberFeed: {
        subscriberName: resolveSubscriberName(prefillFoundMember),
        planAcronym: selectedSubClient.planAcronym,
        clientId: selectedSubClient.client.specifiedId,
        subClientId: selectedSubClient.specifiedId,
        asOf: selectedSubClient.asOfDate,
        memberId: resolvedMemberId ?? '',
        pseudoSsnInd: this.findMemberFeed()?.pseudoSsnInd === true,
        subscriberEnrollmentID: resolvedSubscriberEnrollmentId,
        foundMember: prefillFoundMember,
        ...this.getFamilyEnrollmentDefaults(isFamilyEnrollmentFlow, familyDefaults)
      },
      event: isAddFlowEvent ? 'add flow' : event,
      nextRoute,
      selectedSubscriber: prefillFoundMember
        ? buildSelectedSubscriber(
          prefillFoundMember,
          resolveSubscriberEnrollmentId(prefillFoundMember)
        )
        : {}
    });

  }

  private getFamilyEnrollmentDefaults(isFamilyEnrollmentFlow: boolean, familyDefaults: any): Record<string, unknown> {
    if (!isFamilyEnrollmentFlow) {
      return {};
    }

    return {
      eligibilityEffectiveDate: familyDefaults.eligibilityEffectiveDate
        ? moment(familyDefaults.eligibilityEffectiveDate).format('YYYY-MM-DD')
        : familyDefaults.eligibilityEffectiveDate,
      receivedDate: familyDefaults.receivedDate
        ? moment(familyDefaults.receivedDate).format('YYYY-MM-DD')
        : familyDefaults.receivedDate,
      qualifierType: familyDefaults.qualifierType,
      memberBenefitType: familyDefaults.memberBenefitType,
      eligibilityStatusReason: familyDefaults.eligibilityStatusReason,
      eligibilityStatus: familyDefaults.eligibilityStatus
    };
  }

  handleDefaultsFormChange(): void {
    if (!this.setDefaultsForm()?.valid) {
      this.showSsnFormForFamilyEnroll.set(false);
      this.disableSetDefaults.set(false);
    }
  }

  setFamilyEnrollDefaultsForm(): void {
    this.setDefaultsForm.set(this.fb.group({
      receivedDate: [{ value: moment(new Date()).format('MM/DD/YYYY'), disabled: false }, [Validators.required, futureDate()]],
      eligibilityEffectiveDate: ['', Validators.required],
      eligibilityStatus: ['active', Validators.required],
      eligibilityStatusReason: [this.eligStatusReasons().find(reason => reason.value === 'New') ? 'New' : '', Validators.required],
      qualifierType: [''],
      memberBenefitType: ['']
    }));
    this.handleDefaultsFormChange();
  }

  setDefaultsForFamilyEnroll(): void {
    this.showSsnFormForFamilyEnroll.set(true);
    this.disableSetDefaults.set(true);
    this.setConfirmSsnForm();
    if (this.memberIdEntryMode() === 'system') {
      this.generatePseudoSsn();
    }
  }

  ngOnDestroy(): void {
    this.clientSearch.destroy();
    this.clearPendingSubClientSelection();
    this.clearPendingFoundMemberResolution();
    this.clearCopyErrorTimer();
  }

  private applySubClientSelection(selectedSubClients: SubClientSelection[]): void {
    this.invalidateStrictAuthorizationRequest();
    this.pendingFoundMemberEvent.set(null);
    this.clearPendingFoundMemberResolution();
    this.cachedFoundMemberAtSearchStart = null;
    this.selectedSubClients.set(selectedSubClients);
    this.familyEnrollmentAuthorized.set(true);
    this.findMemberFeed.set(null);
    this.displayAddMemberForm.set(false);
    this.showSsnFormForFamilyEnroll.set(false);

    const stateObj = selectedSubClients.length ? {
      sourceClient: selectedSubClients[0].client,
      sourceSubClients: selectedSubClients
    } : {};
    this.setMassActionStore.emit({
      flow: this.subHeader(),
      massActionState: stateObj,
      selectedSubClient: selectedSubClients[0]
    });
    this.setMemberIdEntryMode(selectedSubClients[0]);

    this.resetForms();
    this.disableSetDefaults.set(false);

    if (!selectedSubClients.length) {
      this.displayAddMemberForm.set(false);
      return;
    }

    const selectedSubClient = selectedSubClients[0];
    const appContext = `${this.selectedAdminPlan()}/${selectedSubClient.client.planAcronym}/${selectedSubClient.client.specifiedId}/${selectedSubClient.specifiedId}`;
    const flow = this.subHeader() === 'Family Enrollment' ? 'enrollFamily' : 'addMember';
    this.callStrictAuthoriztions(appContext, flow);
  }

  private setMemberIdEntryMode(selectedSubClient: any): void {
    const ssnSource = selectedSubClient?.personIdentifierSources?.find(source => source?.type === 'SSN');
    const sources = Array.isArray(ssnSource?.sources) ? ssnSource.sources : [];
    const hasGroupAssignedSource = sources.includes('GROUP ASSIGNED');
    const hasSystemAssignedSource = sources.includes('SYSTEM ASSIGNED');
    let memberIdEntryMode: MemberIdEntryMode = 'manual';

    if (hasGroupAssignedSource && hasSystemAssignedSource) {
      memberIdEntryMode = 'both';
    } else if (hasSystemAssignedSource) {
      memberIdEntryMode = 'system';
    }

    this.memberIdEntryMode.set(memberIdEntryMode);
    this.pseudoSsnError.set('');
  }

  private resolveAddFlowFoundMember(event: unknown, rawFoundMember: unknown, normalizedFoundMember: any): any {
    if (!normalizedFoundMember) {
      return null;
    }

    if (typeof event !== 'string') {
      return normalizedFoundMember;
    }

    return rawFoundMember === this.cachedFoundMemberAtSearchStart
      ? null
      : normalizedFoundMember;
  }

  private resolveCurrentFoundMember(): unknown {
    const persistedFoundMember = this.readPersistedFoundMember();
    if (persistedFoundMember) {
      return persistedFoundMember;
    }

    return this.cachedFoundMember();
  }

  private hasManageEligibilityPermission(uiAuth: IUiAuthorizations | null, permission: string): boolean {
    return !!Object.keys(uiAuth || {}).length && !!uiAuth?.manageeligibility?.includes(permission);
  }

  private clearPendingSubClientSelection(): void {
    if (this.pendingSubClientSelectionRef) {
      this.pendingSubClientSelectionRef.destroy();
      this.pendingSubClientSelectionRef = null;
    }

    this.pendingSubClientSelection = null;
  }

  private matchesPendingSubClientSelectionClient(event: unknown): boolean {
    if (!this.pendingSubClientSelection?.length || !event || Array.isArray(event)) {
      return false;
    }

    const pendingClient = this.pendingSubClientSelection[0]?.client;
    const selectedClient = this.resolveClientSelection(event);

    return !!pendingClient
      && !!selectedClient
      && `${selectedClient.planAcronym ?? ''}` === `${pendingClient.planAcronym ?? ''}`
      && `${selectedClient.specifiedId ?? ''}` === `${pendingClient.specifiedId ?? ''}`;
  }

  private resolveClientSelection(event: unknown): { planAcronym?: string; specifiedId?: string } | null {
    if (!event || typeof event !== 'object') {
      return null;
    }

    const selectedClient = event as { planAcronym?: string; specifiedId?: string; client?: { planAcronym?: string; specifiedId?: string } };
    return {
      planAcronym: selectedClient.planAcronym ?? selectedClient.client?.planAcronym,
      specifiedId: selectedClient.specifiedId ?? selectedClient.client?.specifiedId
    };
  }

  private invalidateStrictAuthorizationRequest(): void {
    this.strictAuthorizationRequestVersion++;
    this.strictUiAuthLoading.set(false);
  }

  private resetForms(): void {
    this.addMemberForm()?.reset();
    this.setDefaultsForm()?.reset();
    this.addMemberForm.set(null);
    this.setDefaultsForm.set(null);
  }

  private clearCopyErrorTimer(): void {
    this.copyErrorTimer = clearVisibilityTimer(this.copyErrorTimer);
  }

  private clearPendingFoundMemberResolution(): void {
    this.pendingFoundMemberResolutionRef?.destroy();
    this.pendingFoundMemberResolutionRef = null;
  }

  private schedulePendingFoundMemberResolution(): void {
    this.clearPendingFoundMemberResolution();
    this.pendingFoundMemberResolutionRef = afterNextRender(() => {
      this.pendingFoundMemberResolutionRef = null;
      const latestPendingFoundMemberEvent = this.pendingFoundMemberEvent();
      if (!latestPendingFoundMemberEvent) {
        this.pendingFoundMemberResolutionAttempts = 0;
        return;
      }

      if (this.shouldRetryPendingFoundMemberResolution(latestPendingFoundMemberEvent)) {
        this.pendingFoundMemberResolutionAttempts++;
        this.pendingFoundMemberResolutionTick.update(tick => tick + 1);
        return;
      }

      this.pendingFoundMemberEvent.set(null);
      this.pendingFoundMemberResolutionAttempts = 0;
      this.setAddMemberFeed(latestPendingFoundMemberEvent);
    }, { injector: this.injector });
  }

  private shouldRetryPendingFoundMemberResolution(event: unknown): boolean {
    return typeof event === 'string'
      && this.pendingFoundMemberResolutionAttempts < 5
      && this.resolveCurrentFoundMember() === this.cachedFoundMemberAtSearchStart;
  }

  private readPersistedFoundMember(): unknown {
    const memberDetailsState = JSON.parse(sessionStorage.getItem(MEMBER_DETAILS_STORAGE_KEY) || '{}');
    return memberDetailsState?.[MEMBER_DETAILS_BMT_KEY]?.findMemberState?.foundMember ?? null;
  }

  private clearPersistedFoundMember(): void {
    const memberDetailsState = JSON.parse(sessionStorage.getItem(MEMBER_DETAILS_STORAGE_KEY) || '{}');
    if (!memberDetailsState?.[MEMBER_DETAILS_BMT_KEY]) {
      return;
    }

    const nextMemberDetailsState = {
      ...memberDetailsState,
      [MEMBER_DETAILS_BMT_KEY]: {
        ...(memberDetailsState?.[MEMBER_DETAILS_BMT_KEY] || {}),
        findMemberState: {
          ...(memberDetailsState?.[MEMBER_DETAILS_BMT_KEY]?.findMemberState || {}),
          foundMember: null
        }
      }
    };

    sessionStorage.setItem(MEMBER_DETAILS_STORAGE_KEY, JSON.stringify(nextMemberDetailsState));
  }

  private buildMemberDetailsFeed(selectedSubscriber: any): MemberDetailsFeed | null {
    const planAcronym = selectedSubscriber?.planAcronym;
    const clientId = selectedSubscriber?.clientSpecifiedIdentifier;
    const subClientId = selectedSubscriber?.subclientSpecifiedIdentifier;
    const foundMember = normalizeFoundMemberForPrefill(selectedSubscriber);

    if (!planAcronym || !clientId || !subClientId) {
      return null;
    }

    return {
      subscriberName: resolveSubscriberName(selectedSubscriber),
      planAcronym,
      clientId,
      subClientId,
      memberId: '',
      subscriberEnrollmentID: selectedSubscriber?.subscriberEnrollmentId ?? selectedSubscriber?.subscriberEnrollmentID ?? '',
      ...(foundMember && { foundMember }),
      asOf: selectedSubscriber?.asOf ?? ''
    };
  }

  private syncSingleAvailableSubgroupSelection(availableSubClients: Array<{ specifiedId?: string }>): void {
    if (this.subHeader() === 'Member'
      || this.selectedSubClients().length > 0
      || this.pendingSubClientSelectionRef !== null
      || availableSubClients.length !== 1) {
      return;
    }

    const singleSubClientSpecifiedId = availableSubClients[0]?.specifiedId ?? '';
    const subGroupControl = this.clientSearchComponent?.searchForm?.get('subGroupInfo');
    if (!subGroupControl || !singleSubClientSpecifiedId || subGroupControl.value === singleSubClientSpecifiedId) {
      return;
    }

    subGroupControl.setValue(singleSubClientSpecifiedId);
    subGroupControl.markAsTouched();
    subGroupControl.updateValueAndValidity();
  }

}
